import React, { useEffect, useState } from 'react'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { captureException } from '@sentry/gatsby'
import { useFormik } from 'formik'
import classNames from 'classnames'
import { KeyValuePair } from '@toasttab/shared/src/interfaces/Interface.d'
import { getInitialFormValues } from '@toasttab/shared/src/hooks/useDataCapture'
import {
  extractFirstNameLastName,
  sha1encode
} from '@toasttab/shared/src/utils/dataCaptureUtils'
import { useGeoLocation } from '@toasttab/shared/src/hooks/useGeoLocation'
import {
  getUtmParams,
  localizeFormError,
  postForm,
  getBrowserLanguage
} from '@toasttab/shared/src/utils/formUtils'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import {
  Field,
  Statement
} from '@toasttab/shared/src/components/molecules/Contentful/FormStep/FormStep'
import {
  FormDisclaimerOptions,
  FormDisclaimerOptionsInverse
} from '@toasttab/shared/src/utils/Contentful/richText'
import ThankYou from '@toasttab/shared/src/components/molecules/Contentful/ThankYou/ThankYou'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import PrivacyStatement from '@toasttab/main/src/components/Common/PrivacyStatement'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import FormStep from './FormStep'

export interface MainFormContentfulProps {
  ajaxTyCtas?: any
  ajaxTyDescription?: any
  ajaxTyHeading?: string
  ajaxTyIcon?: any
  ajaxTyShareIcons?: any
  ajaxTyShareIconsLabel?: string
  chiliPiperRouter?: string
  color?: 'default' | 'inverse'
  componentContext: 'ContentfulMdsShowcaseForm' | 'ContentfulMdsHero5050'
  description: string
  disclaimer: any
  entryId?: string
  fieldIdPrefix?: string
  formCookie?: string
  formSteps: any
  heading: string
  heapInteractionSubmit?: string
  isProgressiveEligibleForm?: boolean
  layout?: 'default' | 'inline'
  marketoId: string
  marketoProgramName: string
  region?: string
  removePostalCode?: boolean
  submitLabel: string
  thankYouPageURL?: string
  formActionURL?:
    | 'marketo-integration/default/referral'
    | 'marketo-integration/default/post'
}

const MainForm = ({
  ajaxTyCtas,
  ajaxTyDescription,
  ajaxTyHeading,
  ajaxTyIcon,
  ajaxTyShareIcons,
  ajaxTyShareIconsLabel,
  chiliPiperRouter = '',
  color = 'default',
  componentContext,
  description,
  disclaimer,
  entryId = '',
  fieldIdPrefix,
  formCookie,
  formSteps,
  heading,
  heapInteractionSubmit,
  isProgressiveEligibleForm,
  layout = 'default',
  marketoId,
  marketoProgramName,
  region = 'US',
  removePostalCode,
  submitLabel,
  formActionURL = 'marketo-integration/default/post',
  thankYouPageURL
}: MainFormContentfulProps) => {
  const [countryCode, setCountryCode] = useState(null)
  const [formActionUrl, setFormActionUrl] = useState(
    process.env.GATSBY_FORM_ACTION_URL
  )
  const [isAjax, setIsAjax] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [postalCode, setPostalCode] = useState(null)
  const [showThankYouAjax, setShowThankYouAjax] = useState(false)
  const [utmParameters, setUtmParameters] = useState({})

  const { geoData, loading } = useGeoLocation()
  const { t, prefix } = useI18next()

  const intlBottomForm =
    componentContext === 'ContentfulMdsShowcaseForm' && region !== 'US'
  const usBottomForm =
    componentContext === 'ContentfulMdsShowcaseForm' && region === 'US'

  const disclaimerOptions =
    color === 'inverse' ? FormDisclaimerOptionsInverse : FormDisclaimerOptions

  useEffect(() => {
    // Add logic if there is no thankYouPageURL
    setIsAjax(ajaxTyHeading && ajaxTyDescription)
  }, [])

  useEffect(() => {
    if (geoData) {
      setPostalCode(geoData?.postal?.code)
      setCountryCode(geoData?.country?.iso_code)
    }
  }, [loading])

  useEffect(() => {
    if (window?.location) {
      let formAction = process.env.GATSBY_FORM_ACTION_URL

      if (formActionURL === 'marketo-integration/default/referral') {
        const actionURL =
          process.env.GATSBY_FORM_ACTION_URL.split('?action=')[0]
        formAction = `${actionURL}?action=${formActionURL}`
      }
      if (
        [
          'contentful-dev.phrasingpro3.com',
          'contentful-staging.phrasingpro3.com',
          'contentful-preview-dev.phrasingpro3.com',
          'contentful-preview-staging.phrasingpro3.com'
        ].includes(window.location.hostname)
      ) {
        if (formActionURL === 'marketo-integration/default/referral') {
          formAction = `?action=${formActionURL}`
        }
        let domain = 'https://staging.phrasingpro3.com'
        if (
          ['contentful-staging.phrasingpro3.com'].includes(
            window.location.hostname
          )
        ) {
          domain = `https://${window.location.hostname}/`
        }
        formAction = `${domain}${formAction}`
      }
      if (
        [
          'contentful-preview-master.phrasingpro3.com',
          'contentful-pos-proxy.phrasingpro3.com'
        ].includes(window.location.hostname)
      ) {
        formAction = `https://pos.toasttab.com${formAction}`
      }
      if (formAction !== formActionUrl) {
        setFormActionUrl(formAction)
      }
    }
  }, [])

  useEffect(() => {
    const utmParams = getUtmParams()
    if (Object.keys(utmParams).length > 0) {
      setUtmParameters(utmParams)
    }
  }, [])

  const progressiveCookie = _helpers.getCookie('progressiveEnabled')
  const isProgressiveForm = progressiveCookie

  // dynamically create initialValues for Formik
  const initValues: KeyValuePair = {
    _mkt_trk: `${_helpers.getCookie('_mkto_trk')}`,
    _mktoReferrer: `${typeof window !== 'undefined' && window.location.href}`,
    appendVariantTestText: '',
    browserLanguage: getBrowserLanguage(),
    company: _helpers.getCookie('company'),
    cR: chiliPiperRouter, // Not currently in use anywhere, but needed in data
    // craftFieldsetSlug: formCategory.slug, // what do we do here? we don't have the same slug fields
    craftFormLocation: 'NOT SET',
    craftJavascriptStatus: 'Loaded',
    craftRestaurantToast: '', // honeypot, purposefully empty, if this is filled then assuming it's bot
    eI: entryId,
    email: _helpers.getCookie('email'),
    fC: formCookie,
    formid: marketoId,
    gae: '',
    isAjax: isAjax,
    mkoFullName: _helpers.getCookie('fullName'),
    mkoProgramName: marketoProgramName,
    munchkinId: `${process.env.GATSBY_FORM_MUNCHKINID ?? '713-DII-842'}`,
    phone: _helpers.getCookie('phonenumber'),
    postalCode,
    ...(marketoId !== '1024' && { qQVersion: 'short-demo-request' }),
    recaptchaValid: 'no',
    referrerURL: `${process.env.GATSBY_CMS_URL}${
      typeof window !== 'undefined' && window.location.pathname
    }`,
    referringPageURLText: `${
      typeof document !== 'undefined' && document.referrer
    }`,
    returnURL: thankYouPageURL,
    ...(marketoId !== '1024' && { shownCalendly: '' }),
    step: 0,
    ...(marketoId !== '1024' && { toastType: '' })
  }
  // set up additional standard initial marketo form values
  const { initialValues } = getInitialFormValues(initValues)

  const validateFormSubmission = (values: KeyValuePair) => {
    const errors: KeyValuePair = {}

    const coreFields = formSteps[formik.values.step].coreFields
    coreFields?.forEach((field: Field | Statement) => {
      if (field.__typename === 'ContentfulMdsFormField') {
        const f = field as Field
        const fieldName = f.fieldInputNameMarketoRestApi
        if (f.isRequired && !values[fieldName]) {
          errors[fieldName] = localizeFormError(
            t('formField.required'),
            f.fieldLabel
          )
        } else if (f.fieldType === 'Email') {
          if (_helpers.validateEmail(values[fieldName])) {
            errors.email = localizeFormError(
              t('formField.invalid'),
              f.fieldLabel
            )
          } else {
            // @ts-ignore - Typescript doesn't like setting new properties on window
            const heapHandler = window.heap
            if (
              typeof window !== 'undefined' &&
              typeof heapHandler !== 'undefined'
            ) {
              heapHandler.addUserProperties({
                email: values[fieldName].toLowerCase()
              })
              heapHandler.identify(values[fieldName].toLowerCase())
            }
          }
        } else if (f.fieldType === 'Phone') {
          // currently otl doesnt validate phone
          const phoneInput = fieldName
          if (
            _helpers.validatePhoneInput(
              values[phoneInput],
              region.toLowerCase()
            )
          ) {
            errors.phone = localizeFormError(
              t('formField.invalid'),
              f.fieldLabel
            )
          } else if (['ca', 'us'].includes(region.toLowerCase())) {
            const formattedPhone = _helpers.formatPhoneNumber(
              values[phoneInput]
            )
            if (formattedPhone !== values[phoneInput]) {
              formik.setFieldValue(phoneInput, formattedPhone)
            }
          }
        } else if (fieldName === 'mkoFullName') {
          const fieldValue = values[fieldName]
          if (_helpers.validateFullName(fieldValue, prefix)) {
            errors[f.fieldInputNameMarketoRestApi] = localizeFormError(
              t('formField.required'),
              f.fieldLabel
            )
          }
        }
      }
    })
    return errors
  }

  const renderFormSteps = (formSteps: any) => {
    const numSteps = formSteps.length
    return formSteps.map((step: any, index: number) => {
      return (
        <FormStep
          {...step}
          color={color}
          componentContext={componentContext}
          fieldIdPrefix={fieldIdPrefix}
          formik={formik}
          intlBottomForm={intlBottomForm}
          isLastStep={index + 1 === formSteps.length}
          isProgressiveEligibleForm={isProgressiveEligibleForm}
          isProgressiveForm={isProgressiveForm}
          isSubmitting={isSubmitting}
          key={index}
          layout={layout}
          region={region}
          submitLabel={submitLabel}
          usBottomForm={usBottomForm}
          isActive={
            (!showThankYouAjax && index === formik.values.step) ||
            (showThankYouAjax && index + 1 === numSteps) ||
            (index === 0 && numSteps === 1)
          }
        />
      )
    })
  }

  // Pass the useFormik() hook initial form values and a submit function that will
  // be called when the form is submitted
  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: false,
    validate: validateFormSubmission,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => {
      const extractedName = extractFirstNameLastName(values['mkoFullName'])
      values['firstName'] = extractedName.FirstName
      values['lastName'] = extractedName.LastName

      const formData = new FormData()
      formData.append('craftInferredCountry', _helpers.getCountry(countryCode))

      for (const value in values) {
        if (value === 'mkoFullName') {
          if (extractedName.FirstName) {
            _helpers.createCookie('fN', extractedName.FirstName, 525600, '/')
          }
          if (extractedName.LastName) {
            _helpers.createCookie('lN', extractedName.LastName, 525600, '/')
          }
          if (extractedName.FullName) {
            _helpers.createCookie(
              'fullName',
              extractedName.FullName,
              525600,
              '/'
            )
          }
        } else if (value === 'email') {
          _helpers.createCookie('email', values[value], 525600, '/')
        } else if (value === 'company') {
          _helpers.createCookie('company', values[value], 525600, '/')
        } else if (value === 'phone') {
          _helpers.createCookie('phonenumber', values[value], 525600, '/')
        } else if (value === 'postalCode') {
          _helpers.createCookie('_userUpdatedLoc', values[value], 525600, '/')
        } else if (value === 'mkoProgramName') {
          if (
            values[value].toLowerCase() === 'Website-Demo-Request'.toLowerCase()
          ) {
            _helpers.createCookie('toastDemoForm', '1', 525600, '/')
          }
        } else {
          _helpers.createCookie('toastResourceForm', '1', 900, '/')
          _helpers.createCookie('progressiveEnabled', '1', 43200, '/')
        }
        // Only when Toast Type exists
        if (values['toastType']) {
          let regionString = ''
          if (region.toLowerCase() !== 'us') {
            regionString = `/${region.toLowerCase()}`
          }
          if (value === 'returnURL') {
            let returnUrl = `${regionString}/request-demo-confirmed`
            if (values['toastType'] === "I'm already a Toast customer") {
              returnUrl = `${regionString}/thank-you/toast-confirmed-customer`
            } else if (values['toastType'] === "I'm a restaurant guest") {
              returnUrl = `${regionString}/thank-you/toast-confirmed`
            }
            formData.append(value, returnUrl)
          } else {
            const formValue = [null, undefined, 'null|null'].includes(
              values[value]
            )
              ? ''
              : values[value]
            formData.append(value, formValue)
          }

          if (
            values['toastType'] === "I'm interested in buying Toast" ||
            values['toastType'] === 'I’m interested in restaurant technology' ||
            values['toastType'] ===
              "I'm interested in buying Toast for restaurant"
          ) {
            const irclickid = _helpers.getCookie('irclickid') || null
            if (
              typeof window !== 'undefined' &&
              (window as any).ire &&
              irclickid &&
              (window as any).heap
            ) {
              sha1encode(values['email'] || '').then((emailSha1: string) => {
                ;(window as any).ire(
                  'trackConversion',
                  23048,
                  {
                    orderId: window.heap.userId,
                    clickId: irclickid,
                    customerEmail: emailSha1
                  },
                  {
                    verifySiteDefinitionMatch: true
                  }
                )
              })
            }
          }
        } else {
          const formValue = [null, undefined, 'null|null'].includes(
            values[value]
          )
            ? ''
            : values[value]
          formData.append(value, formValue)
        }
      }

      if (!isAjax) {
        // We need to delete this value entirely for the POST request
        // If it's set at all, even to null or false, it triggers some unwanted PHP and fails the form submission
        formData.delete('isAjax')
      }

      setIsSubmitting(true)

      if (isAjax) {
        formData.append('responseTemplate', '') // This is a hack to get the form to submit without a response template to cause no errors in the console.
        setShowThankYouAjax(true)
      }
      postForm(formData, formActionUrl)
        .then((response) => {
          setIsSubmitting(false)
          if (response.redirected) {
            setIsSubmitted(true)
            // navigate(response.url.replace('https://phrasingpro3.com', ''))
            // @ts-ignore
            // Typescript doesn't like setting a window.location as a string, despite it being common practice
            // https://github.com/microsoft/TypeScript/issues/48949
            window.location = response.url
          }
        })
        .catch(function (err) {
          console.log(err)
          captureException(`${err} - ${JSON.stringify(values, null, 2)}`)
          setIsSubmitting(false)
          setIsError(true)
        })
    }
  })

  useEffect(() => {
    const { initialValues } = getInitialFormValues(initValues, utmParameters)
    formik.setValues({ ...initialValues }, false)
  }, [utmParameters])

  const formHeadingClasses =
    region !== 'US' ? 'text-center [@media(min-width:992px)]:text-left' : ''

  const containerCss = classNames('px-4 md:px-6', {
    'rounded-[.625rem] py-5 md:py-8':
      componentContext === 'ContentfulMdsHero5050' || region !== 'US',
    'bg-gray-0': componentContext === 'ContentfulMdsHero5050',
    hidden: showThankYouAjax,
    block: !showThankYouAjax
  })

  return (
    <>
      <div
        className={`${containerCss} px-4 md:px-6 {showThankYouAjax ? 'hidden' : 'block'}`}
      >
        {componentContext === 'ContentfulMdsShowcaseForm' && heading ? (
          <Typography
            as='h3'
            classes='text-[1.5rem] mb-4 leading-[1]'
            variation='h3'
          >
            {heading}
          </Typography>
        ) : null}
        {componentContext === 'ContentfulMdsHero5050' && heading ? (
          <Typography
            as='h2'
            classes={`!text-[1.5rem] pb-6 [@media(max-width:767px)]:pb-4 !leading-[1] ${formHeadingClasses}`}
            variation='h2'
          >
            {heading}
          </Typography>
        ) : null}
        {description ? (
          <Typography as='p' classes='text-[#666] mb-4'>
            {description}
          </Typography>
        ) : null}
        <form
          data-heap-
          data-heap-mktform-id={marketoId}
          data-jest-target='main-form'
          data-testid={isSubmitted ? 'form-submitted' : 'form-not-submitted'}
          id={`mktForm_${marketoId}`}
          onSubmit={(e) => {
            e.preventDefault()
            if (formik.isValid) {
              formik.submitForm()
            }
          }}
        >
          {formSteps.length > 0 && renderFormSteps(formSteps)}
          <input
            id='GCLID__c'
            name='GCLID__c'
            type='hidden'
            value={formik.values.GCLID__c ?? ''}
          />
          <input
            id='Website_Source_Detail__c'
            name='Website_Source_Detail__c'
            type='hidden'
            value={formik.values.Website_Source_Detail__c ?? ''}
          />
          <input
            id='craftFieldsetSlug'
            name='craftFieldsetSlug'
            type='hidden'
            value={formik.values.craftFieldsetSlug ?? ''}
          />
          <input
            id='Last_Blog_Seen__c'
            name='Last_Blog_Seen__c'
            type='hidden'
            value={formik.values.Last_Blog_Seen__c ?? ''}
          />
          <input
            id='Web_initiative__c'
            name='Web_initiative__c'
            type='hidden'
            value={formik.values.Web_initiative__c ?? ''}
          />
          <input
            id='Web_origin__c'
            name='Web_origin__c'
            type='hidden'
            value={formik.values.Web_origin__c ?? ''}
          />
          <input
            id='Web_detail__c'
            name='Web_detail__c'
            type='hidden'
            value={formik.values.Web_detail__c ?? ''}
          />
          <input
            id='_mktoReferrer'
            name='_mktoReferrer'
            type='hidden'
            value={formik.values._mktoReferrer ?? ''}
          />
          <input
            id='pageFormSubmissionURL'
            name='pageFormSubmissionURL'
            type='hidden'
            value={formik.values.pageFormSubmissionURL ?? ''}
          />
          <input
            id='eI'
            name='eI'
            type='hidden'
            value={formik.values.eI ?? ''}
          />
          <input
            id='browserLanguage'
            name='browserLanguage'
            type='hidden'
            value={formik.values.browserLanguage ?? ''}
          />
          <input
            id='referrerURL'
            name='referrerURL'
            type='hidden'
            value={formik.values.referrerURL ?? ''}
          />
          <input
            id='returnURL'
            name='returnURL'
            type='hidden'
            value={formik.values.returnURL}
          />
          <input
            id='munchkinId'
            name='munchkinId'
            type='hidden'
            value={formik.values.munchkinId}
          />
          <input
            id='_mkt_trk'
            name='_mkt_trk'
            type='hidden'
            value={formik.values._mkt_trk}
          />
          <input
            id='mkoProgramName'
            name='mkoProgramName'
            type='hidden'
            value={formik.values.mkoProgramName}
          />
          <input name='formid' type='hidden' value={formik.values.formid} />
          <input
            id='craftInferredCountry'
            name='craftInferredCountry'
            type='hidden'
            value={_helpers.getCountry(countryCode) ?? ''}
          />
          {!removePostalCode && (
            <input
              id='postalCode'
              name='postalCode'
              type='hidden'
              value={postalCode ?? ''}
            />
          )}
          {marketoId !== '1024' && (
            <input
              id='qQVersion'
              name='qQVersion'
              type='hidden'
              value={formik.values.qQVersion ?? ''}
            />
          )}
          <input
            id='fC'
            name='fC'
            type='hidden'
            value={formik.values.fC ?? ''}
          />
          <input
            id='gae'
            name='gae'
            type='hidden'
            value={formik.values.gae ?? ''}
          />
          <input
            id='reffering-page-url'
            name='referringPageURLText'
            type='hidden'
            value={formik.values.referringPageURLText}
          />
          <input
            id='craftJavascriptStatus'
            name='craftJavascriptStatus'
            type='hidden'
            value={formik.values.craftJavascriptStatus}
          />
          {marketoId !== '1024' && (
            <input
              id='shownCalendly'
              name='shownCalendly'
              type='hidden'
              value={formik.values.shownCalendly}
            />
          )}
          <input
            id='recaptchaValid'
            name='recaptchaValid'
            type='hidden'
            value={formik.values.recaptchaValid}
          />
          <input
            id='progressiveEligible'
            name='progressiveEligible'
            type='hidden'
            value={formik.values.progressiveEligible}
          />
          <input
            id='appendVariantTestText'
            name='appendVariantTestText'
            type='hidden'
            value={formik.values.appendVariantTestText}
          />
          <input
            id='craftFormLocation'
            name='craftFormLocation'
            type='hidden'
            value={formik.values.craftFormLocation}
          />
          <input id='cR' name='cR' type='hidden' value={formik.values.cR} />
          <input
            id='craftRestaurantToast'
            name='craftRestaurantToast'
            type='hidden'
            value=''
          />
          <input
            id='action'
            name='action'
            type='hidden'
            value={formActionUrl}
          />
        </form>
        {isError ? (
          <div className='text-error text-xs relative text-left top-0'>
            There was an error trying to submit the form.
          </div>
        ) : null}
        {disclaimer ? (
          <div className='mt-4'>
            {renderRichText(disclaimer, disclaimerOptions)}
          </div>
        ) : null}
        {region === 'US' && <PrivacyStatement />}
      </div>
      {isAjax ? (
        <ThankYou
          ajaxTyCtas={ajaxTyCtas}
          ajaxTyDescription={ajaxTyDescription}
          ajaxTyHeading={ajaxTyHeading}
          ajaxTyIcon={ajaxTyIcon}
          ajaxTyShareIcons={ajaxTyShareIcons}
          ajaxTyShareIconsLabel={ajaxTyShareIconsLabel}
          showThankyou={showThankYouAjax}
        />
      ) : null}
    </>
  )
}

export default MainForm
